import React, {useState, useEffect, useMemo, useCallback} from 'react';
import {
  Modal,
  Layout,
  Tabs,
  Select,
  Divider,
  Row,
  Col,
  message,
  Button
} from 'antd';
import {
  BarChartOutlined,
  LineChartOutlined,
  PieChartOutlined,
  AreaChartOutlined,
  DotChartOutlined,
  RadarChartOutlined,
  AlignLeftOutlined
} from '@ant-design/icons';
import {SheetInfo, Field, ChartConfig} from '../../types';
import {VChart} from '@visactor/react-vchart';
import { processChartData } from '../../utils/chartDataProcessor';
import sheetService from '../../services/sheetService';
import './style.css';

const {Sider, Content} = Layout;

interface ChartConfigModalProps {
  visible: boolean;
  onClose: () => void;
  onAddChart: (chartConfig: any) => void;
  allSheetList: SheetInfo[];
  editingChart?: ChartConfig | null;
}

export const ChartConfigModal: React.FC<ChartConfigModalProps> = ({
  visible,
  onClose,
  onAddChart,
  allSheetList,
  editingChart = null
}) => {
  const [activeTab, setActiveTab] = useState('data');

  // --- Chart Configuration State ---
  const [chartType, setChartType] = useState('bar');
  const [dataSourceId, setDataSourceId] = useState<string | null>(null);
  const [fields, setFields] = useState<Field[]>([]);
  const [records, setRecords] = useState<any[]>([]);

  const [xAxisField, setXAxisField] = useState<string | null>(null);
  const [yAxisField, setYAxisField] = useState<string | null>(null);
  const [seriesField, setSeriesField] = useState<string | null>(null); // 二级类别/图例

  // 添加一个状态来控制图表渲染
  const [shouldRenderChart, setShouldRenderChart] = useState(false);
  // 添加标题相关状态
  const [chartTitle, setChartTitle] = useState('');
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  // 图表类型配置
  const chartTypes = [
    {
      key: 'bar',
      label: '柱状图',
      icon: <BarChartOutlined style={{fontSize: '20px'}} />
    },
    {
      key: 'line',
      label: '折线图',
      icon: <LineChartOutlined style={{fontSize: '20px'}} />
    },
    {
      key: 'pie',
      label: '饼图',
      icon: <PieChartOutlined style={{fontSize: '20px'}} />
    },
    {
      key: 'barHorizontal',
      label: '条形图',
      icon: <AlignLeftOutlined style={{fontSize: '20px'}} />
    },
    {
      key: 'area',
      label: '面积图',
      icon: <AreaChartOutlined style={{fontSize: '20px'}} />
    },
    {
      key: 'dot',
      label: '散点图',
      icon: <DotChartOutlined style={{fontSize: '20px'}} />
    },
    {
      key: 'radar',
      label: '雷达图',
      icon: <RadarChartOutlined style={{fontSize: '20px'}} />
    }
  ];
  const isEditMode = editingChart !== null;

  // --- Refs ---

  // 当弹窗打开或编辑图表改变时，填充数据
  useEffect(() => {
    console.log(
      '弹窗打开或编辑图表改变visible',
      visible,
      isEditMode,
      editingChart
    );

    if (visible && isEditMode && editingChart) {
      // 填充编辑模式的数据
      setChartType(editingChart.chartType);
      setDataSourceId(editingChart.dataSource);
      setChartTitle(editingChart.title || '');
    } else if (visible && !isEditMode) {
      // 新建模式，重置所有状态
      setChartType('bar');
      setDataSourceId(null);
      setChartTitle('');
      setXAxisField(null);
      setYAxisField(null);
      setSeriesField(null);
    }
  }, [visible, isEditMode, editingChart]);

  // 当弹窗关闭时重置状态
  useEffect(() => {
    if (!visible) {
      setTimeout(() => {
        setActiveTab('data');
        setChartType('bar');
        setDataSourceId(null);
        setFields([]);
        setRecords([]);
        setXAxisField(null);
        setYAxisField(null);
        setSeriesField(null);
        setShouldRenderChart(false);
        setChartTitle('');
        setIsEditingTitle(false);
      }, 300);
    }
  }, [visible]);

  // 获取选中数据源的字段和数据
  useEffect(() => {
    if (dataSourceId) {
      const fetchSheetData = async () => {
        try {
          // 1. 专门获取字段列表及其类型
          const rawFieldData = await sheetService.getTableFields(dataSourceId);
          console.log('获取到的字段数据:', rawFieldData);

          // 2. 然后对数组调用 .map()，并为参数 f 提供明确的类型
          const fieldData = rawFieldData
            .filter((f: any) => !f.meta?.hidden)
            .map((f: Field) => ({
              field: f.field,
              type: f.type
            }));
          console.log('处理后的字段数据:', fieldData);

          // 2. 获取该工作表的所有记录
          const recordData = await sheetService.getTableData(
            dataSourceId,
            fieldData.map((f: Field) => f.field)
          );
          const processedRecords = recordData.map((record: any) => {
            return {
              ...record,
              记录行数: 1
            };
          });
          fieldData.push({field: '记录行数', type: 'float'}); // 添加一个默认选项
          setFields(fieldData || []);
          setRecords(processedRecords || []);

          if (isEditMode && editingChart) {
            // 从图表规格中提取字段配置
            const spec = editingChart.chartSpec;
            console.log('编辑模式图表规格:', spec);

            // 根据图表类型提取字段
            if (spec.type === 'pie') {
              setXAxisField(spec.categoryField || null);
              setYAxisField(spec.valueField || null);
            } else if (spec.type === 'bar' && spec.direction === 'horizontal') {
              // 水平条形图的字段是交换的
              setXAxisField(spec.yField || null);
              setYAxisField(spec.xField || null);
            } else {
              console.log('qigai', spec.yField, spec.xField, fields);
              setXAxisField(spec.xField || spec.categoryField || null);
              setYAxisField(spec.yField || spec.valueField || null);
            }

            setSeriesField(spec.seriesField || null);
          }
        } catch (error) {
          message.error('获取数据源信息失败');
          setFields([]);
          setRecords([]);
        }
      };
      fetchSheetData();
    } else {
      setFields([]);
      setRecords([]);
    }
    // 重置字段选择
    setXAxisField(null);
    setYAxisField(null);
    setSeriesField(null);
    setShouldRenderChart(false);
  }, [dataSourceId, allSheetList]);

  // 当配置完成时才允许渲染图表
  useEffect(() => {
    if (records.length > 0 && xAxisField && yAxisField) {
      // 延迟渲染，避免立即渲染导致的问题
      const timer = setTimeout(() => {
        setShouldRenderChart(true);
      }, 100);
      return () => clearTimeout(timer);
    } else {
      setShouldRenderChart(false);
      // 显式返回undefined，消除警告
      return undefined;
    }
  }, [records, xAxisField, yAxisField, chartType]);

  // 提取图表配置生成函数
  const generateChartSpec = useCallback(
    (
      chartType: string,
      processedData: any[],
      xAxisField: string,
      yAxisField: string,
      seriesField?: string | null,
      showLegends: boolean = false
    ) => {

      let baseConfig = {
        data: {values: processedData}
        
      };
      console.log('怎么又美金', isEditMode, editingChart);

      if (isEditMode && editingChart) {
        const {axes, color, legends, code, id} = editingChart.chartSpec;
        baseConfig = {
          ...baseConfig,
          ...(axes && {axes}),
          ...(color && {color}),
          ...(legends && {legends}),
          code,
          id
        };
        console.log('编辑模式图表基础配置:', baseConfig);
      }
      // 检查是否需要显示图例
      const hasSeriesField = seriesField && seriesField !== yAxisField;
      const shouldShowLegend = showLegends && hasSeriesField;

      switch (chartType) {
        case 'pie':
          return {
            type: 'pie',
            ...baseConfig,
            categoryField: xAxisField,
            valueField: yAxisField,
            outerRadius: 0.8,
            innerRadius: 0,
            legends: [
              {
                visible: true,
                orient: 'top' as const, // 使用 as const 确保类型正确
                position: 'middle' as const
              }
            ]
          };

        case 'line':
          return {
            type: 'line',
            ...baseConfig,
            xField: xAxisField,
            yField: yAxisField,
            ...(hasSeriesField && {seriesField}),
            point: {visible: true, size: 4},
            ...(shouldShowLegend && {
              legends: [
                {
                  visible: true,
                  orient: 'top' as const,
                  position: 'middle' as const
                }
              ]
            })
          };

        case 'area':
          return {
            type: 'area',
            ...baseConfig,
            xField: xAxisField,
            yField: yAxisField,
            ...(hasSeriesField && {seriesField}),
            ...(shouldShowLegend && {
              legends: [
                {
                  visible: true,
                  orient: 'top' as const,
                  position: 'middle' as const
                }
              ]
            })
          };

        case 'dot':
          return {
            type: 'scatter',
            ...baseConfig,
            xField: xAxisField,
            yField: yAxisField,
            ...(hasSeriesField && {seriesField}),
            ...(shouldShowLegend && {
              legends: [
                {
                  visible: true,
                  orient: 'top' as const,
                  position: 'middle' as const
                }
              ]
            })
          };

        case 'barHorizontal':
          return {
            type: 'bar',
            ...baseConfig,
            xField: yAxisField, // 水平条形图交换x和y
            yField: xAxisField,
            direction: 'horizontal' as const,
            ...(hasSeriesField && {seriesField}),
            ...(shouldShowLegend && {
              legends: [
                {
                  visible: true,
                  orient: 'top' as const,
                  position: 'middle' as const
                }
              ]
            })
          };

        case 'radar':
          return {
            type: 'radar',
            ...baseConfig,
            categoryField: xAxisField,
            valueField: yAxisField,
            radius: 0.8,
            angleField: seriesField || xAxisField, // 使用系列字段或x轴字段作为角度
            legends: shouldShowLegend
              ? [
                  {
                    visible: true,
                    orient: 'top' as const,
                    position: 'middle' as const
                  }
                ]
              : []
          };

        default: // 'bar'
          return {
            type: 'bar',
            ...baseConfig,
            xField: xAxisField,
            yField: yAxisField,
            color: [
              {
                gradient: 'linear',
                x0: 0.01,
                y0: 0,
                x1: 0.01,
                y1: 1,
                stops: [
                  {
                    offset: 0,
                    color: '#1DD0F3FF'
                  },
                  {
                    offset: 1,
                    color: '#1DD0F300'
                  }
                ]
              },
              {
                gradient: 'linear',
                x0: 0.01,
                y0: 0,
                x1: 0.01,
                y1: 1,
                stops: [
                  {
                    offset: 0,
                    color: '#2693FFFF'
                  },
                  {
                    offset: 1,
                    color: '#2693FF00'
                  }
                ]
              },
              {
                gradient: 'linear',
                x0: 0.01,
                y0: 0,
                x1: 0.01,
                y1: 1,
                stops: [
                  {
                    offset: 0,
                    color: '#3259F4FF'
                  },
                  {
                    offset: 1,
                    color: '#3259F400'
                  }
                ]
              },
              {
                gradient: 'linear',
                x0: 0.01,
                y0: 0,
                x1: 0.01,
                y1: 1,
                stops: [
                  {
                    offset: 0,
                    color: '#1B0CA1FF'
                  },
                  {
                    offset: 1,
                    color: '#1B0CA100'
                  }
                ]
              },
              {
                gradient: 'linear',
                x0: 0.01,
                y0: 0,
                x1: 0.01,
                y1: 1,
                stops: [
                  {
                    offset: 0,
                    color: '#CB2BC6FF'
                  },
                  {
                    offset: 1,
                    color: '#CB2BC600'
                  }
                ]
              },
              {
                gradient: 'linear',
                x0: 0.01,
                y0: 0,
                x1: 0.01,
                y1: 1,
                stops: [
                  {
                    offset: 0,
                    color: '#FF581DFF'
                  },
                  {
                    offset: 1,
                    color: '#FF581D00'
                  }
                ]
              },
              {
                gradient: 'linear',
                x0: 0.01,
                y0: 0,
                x1: 0.01,
                y1: 1,
                stops: [
                  {
                    offset: 0,
                    color: '#FBBB16FF'
                  },
                  {
                    offset: 1,
                    color: '#FBBB1600'
                  }
                ]
              },
              {
                gradient: 'linear',
                x0: 0.01,
                y0: 0,
                x1: 0.01,
                y1: 1,
                stops: [
                  {
                    offset: 0,
                    color: '#F6FB17FF'
                  },
                  {
                    offset: 1,
                    color: '#F6FB1700'
                  }
                ]
              },
              {
                gradient: 'linear',
                x0: 0.01,
                y0: 0,
                x1: 0.01,
                y1: 1,
                stops: [
                  {
                    offset: 0,
                    color: '#73EC55FF'
                  },
                  {
                    offset: 1,
                    color: '#73EC5500'
                  }
                ]
              }
            ],
            ...(hasSeriesField && {seriesField}),
            ...(shouldShowLegend && {
              legends: [
                {
                  visible: true,
                  orient: 'top' as const,
                  position: 'middle' as const
                }
              ]
            })
          };
      }
    },
    [isEditMode, editingChart]
  );

  const chartSpec = useMemo(() => {
    if (!shouldRenderChart || !records.length || !xAxisField || !yAxisField) {
      return {
        type: 'bar',
        data: {values: []},
        xField: 'x',
        yField: 'y'
      };
    }

    try {
      const options = {
        records,
        chartSpec:{},
        xAxisField,
        yAxisField,
        seriesField
      }
      const processedData = processChartData(options);
      return generateChartSpec(
        chartType,
        processedData,
        xAxisField,
        yAxisField,
        seriesField,
        true // 改为true，预览时也显示图例
      );
    } catch (error) {
      console.error('生成图表配置时出错:', error);
      return {
        type: 'bar',
        data: {values: [{category: 'Error', value: 0}]},
        xField: 'category',
        yField: 'value',
        animation: false
      };
    }
  }, [
    shouldRenderChart,
    records,
    chartType,
    xAxisField,
    yAxisField,
    seriesField,
    processChartData,
    generateChartSpec,
    isEditMode,
    editingChart
  ]);
  // 简化后的handleAddClick
  const handleAddClick = () => {
    if (!dataSourceId || !xAxisField || !yAxisField) {
      message.warn('请完成图表的基本配置');
      return;
    }

    try {
      // 使用相同的数据处理和配置生成函数
      const options = {
        records,
        chartSpec:{},
        xAxisField,
        yAxisField,
        seriesField
      }
      const processedData = processChartData(options);
      const finalSpec = generateChartSpec(
        chartType,
        processedData,
        xAxisField,
        yAxisField,
        seriesField,
        true
      );

      // const newChart = {
      //   code: `chart-${Date.now()}`,
      //   chartType,
      //   chartSpec: finalSpec,
      //   dataSource: dataSourceId,
      //   title: chartTitle || `${chartType} 图表`,
      //   layout: {i: `chart-${Date.now()}`, x: 0, y: Infinity, w: 6, h: 8}
      // };

      // onAddChart(newChart);
      const chartConfig = {
        code: isEditMode ? editingChart!.code : `chart-${Date.now()}`,
        chartType,
        chartSpec: finalSpec,
        dataSource: dataSourceId,
        title: chartTitle || `${chartType} 图表`,
        layout: isEditMode
          ? editingChart!.layout
          : {i: `chart-${Date.now()}`, x: 0, y: Infinity, w: 6, h: 8}
      };

      onAddChart(chartConfig);
      onClose();
    } catch (error) {
      console.error('创建图表时出错:', error);
      message.error('创建图表失败，请检查数据配置');
    }
  };

  // 处理标题编辑
  const handleTitleClick = () => {
    setIsEditingTitle(true);
  };

  const handleTitleBlur = () => {
    setIsEditingTitle(false);
  };

  const handleTitleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      setIsEditingTitle(false);
    }
  };

  const numericFields = useMemo(
    () => fields.filter(f => f.type === 'float'),
    [fields]
  );

  return (
    <Modal
      open={visible}
      onCancel={onClose}
      onOk={handleAddClick}
      width="70%"
      destroyOnClose
      bodyStyle={{height: '75vh', padding: 0, borderRadius: '8px'}}
      centered
      okText={isEditMode ? '更新' : '添加'}
      cancelText="取消"
      title={isEditMode ? '编辑图表' : '添加图表'}
      className="dashboard-chart-config-modal"
    >
      <Layout className="dashboard-chart-config-layout">
        <Sider
          width="31%"
          className="dashboard-chart-config-sider"
        >
          <Tabs activeKey={activeTab} onChange={setActiveTab} className="dashboard-chart-tabs">
            <Tabs.TabPane tab="数据信息" key="data">
              <div className="dashboard-form-item">
                <span className="dashboard-form-item-label dashboard-form-item-required">
                  图表类型
                </span>
                <div className="button-group">
                  {chartTypes.map(type => (
                    <Button
                      key={type.key}
                      className={`chart-type-button ${
                        chartType === type.key ? 'active' : ''
                      }`}
                      onClick={() => setChartType(type.key)}
                    >
                      {type.icon}
                      <span className="label">{type.label}</span>
                    </Button>
                  ))}
                </div>
              </div>
              <Divider className="dashboard-divider"/>
              <div className="dashboard-form-item">
                <span className="dashboard-form-item-label dashboard-form-item-required">
                  数据源
                </span>
                <Select
                  placeholder="请选择数据源"
                  className="dashboard-select"
                  onChange={setDataSourceId}
                  value={dataSourceId}
                  showSearch
                  filterOption={(input, option) =>
                    (option?.label as string ?? '').toLowerCase().includes(input.toLowerCase())
                  }
                >
                  {allSheetList.map(sheet => (
                    <Select.Option
                      key={sheet.collection}
                      value={sheet.collection}
                      label={sheet.name}
                    >
                      {sheet.name}
                    </Select.Option>
                  ))}
                </Select>
              </div>
              <Divider className="dashboard-divider" />
              <div className="dashboard-form-item">
                <span className="dashboard-form-item-label dashboard-form-item-required">
                  横轴 (类别)
                </span>
                <Select
                    placeholder="请选择类别字段"
                    className="dashboard-select"
                    value={xAxisField}
                    onChange={setXAxisField}
                    disabled={!dataSourceId}
                >
                    {fields
                    .filter(f => f.field !== '记录行数')
                    .map(f => (
                        <Select.Option key={f.field} value={f.field}>
                        {f.field}
                        </Select.Option>
                    ))}
                </Select>
              </div>
              <div className="dashboard-form-item">
                <span className="dashboard-form-item-label">
                  二级类别字段 (可选)
                </span>
                <Select
                    placeholder="请选择二级类别字段"
                    className="dashboard-select"
                    value={seriesField}
                    onChange={setSeriesField}
                    disabled={!dataSourceId}
                    allowClear
                >
                    {fields
                    .filter(f => f.field !== xAxisField && f.field !== '记录行数')
                    .map(f => (
                        <Select.Option key={f.field} value={f.field}>
                        {f.field}
                        </Select.Option>
                    ))}
                </Select>
              </div>
              <div className="dashboard-form-item">
                <span className="dashboard-form-item-label dashboard-form-item-required">
                  纵轴 (值)
                </span>
                <Select
                    placeholder="请选择值字段"
                    className="dashboard-select"
                    value={yAxisField}
                    onChange={setYAxisField}
                    disabled={!dataSourceId}
                >
                    {numericFields.map(f => (
                    <Select.Option key={f.field} value={f.field}>
                        {f.field}
                    </Select.Option>
                    ))}
                </Select>
              </div>
            </Tabs.TabPane>
            <Tabs.TabPane tab="更多配置" key="config">
              <p>更多配置项待开发...</p>
            </Tabs.TabPane>
          </Tabs>
        </Sider>
        <Content className="dashboard-chart-config-content">
          {/* 可编辑标题区域 */}
          <div className="chart-preview-title-editable">
            {isEditingTitle ? (
              <input
                type="text"
                value={chartTitle}
                onChange={(e) => setChartTitle(e.target.value)}
                onBlur={handleTitleBlur}
                onKeyPress={handleTitleKeyPress}
                placeholder="输入图表标题"
                autoFocus
                className="chart-preview-title-input"
              />
            ) : (
              <div
                onClick={handleTitleClick}
                className={`chart-preview-title-display ${
                  !chartTitle ? 'placeholder' : ''
                }`}
              >
                {chartTitle || '点击输入图表标题'}
              </div>
            )}
          </div>

          {/* 图表预览区域 */}
          <div className="chart-preview-container">
            {shouldRenderChart ? (
              <div className="chart-preview-content">
                <VChart
                  key={`${chartType}-${xAxisField}-${yAxisField}-${
                    seriesField || 'none'
                  }`}
                  spec={chartSpec}
                  options={{
                    autoFit: true,
                    onError: (error: any) => {
                      console.error('VChart error:', error);
                      setShouldRenderChart(false);
                    }
                  }}
                />
              </div>
            ) : (
              <div className="chart-preview-placeholder">
                {!dataSourceId
                  ? '请选择数据源'
                  : !xAxisField
                  ? '请选择横轴字段'
                  : !yAxisField
                  ? '请选择纵轴字段'
                  : '正在准备图表预览...'}
              </div>
            )}
          </div>
        </Content>
      </Layout>
    </Modal>
  );
};
